import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  type N26AuthState,
  type N26AuthUserInfo,
  type N26OktaAuth,
} from '@n26/okta-auth-client'

type OktaContextValue = {
  oktaAuth: N26OktaAuth
  authState: N26AuthState | null
  oktaUserInfo: N26AuthUserInfo | null
  isAuthenticated: boolean
}

const OktaContext = createContext<OktaContextValue | null>(null)

type OktaProviderProps = {
  oktaAuth: N26OktaAuth
  children: ReactNode
}

export const OktaProvider = ({ oktaAuth, children }: OktaProviderProps) => {
  const [authState, setAuthState] = useState<N26AuthState | null>(() =>
    oktaAuth.n26AuthStateManager.getAuthState()
  )
  const [oktaUserInfo, setOktaUserInfo] = useState<N26AuthUserInfo | null>(
    null
  )

  useEffect(() => {
    const handler = (nextAuthState: N26AuthState) => {
      setAuthState(nextAuthState)
    }
    oktaAuth.n26AuthStateManager.subscribe(handler)
    oktaAuth.start()

    return () => {
      oktaAuth.n26AuthStateManager.unsubscribe(handler)
    }
  }, [oktaAuth])

  useEffect(() => {
    if (authState?.isAuthenticated && authState.accessToken?.accessToken) {
      oktaAuth
        .getUserInfo()
        .then(setOktaUserInfo)
        .catch(() => setOktaUserInfo(null))
    } else {
      setOktaUserInfo(null)
    }
  }, [authState, oktaAuth])

  const value: OktaContextValue = {
    oktaAuth,
    authState,
    oktaUserInfo,
    isAuthenticated: authState?.isAuthenticated ?? false,
  }

  return <OktaContext.Provider value={value}>{children}</OktaContext.Provider>
}

export const useOkta = (): OktaContextValue => {
  const context = useContext(OktaContext)
  if (!context) throw new Error('useOkta must be used within an OktaProvider')
  return context
}
